import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiFileText, FiSearch, FiDownload, FiPrinter,
  FiShoppingCart, FiDollarSign, FiHash, FiPercent,
  FiChevronUp, FiChevronDown, FiChevronRight, FiBarChart2
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line
} from 'recharts';
import { colors } from '../../theme/colors';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getJobsCached, getEmployeesCached } from '../../utils/masterDataStore';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// Inline styles for the expandable per-invoice line-item sub-table.
const DETAIL_TH = { padding: '4px 8px', fontSize: '11px', fontWeight: 600, borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#666' };
const DETAIL_TD = { padding: '4px 8px', borderBottom: '1px solid #f1f5f9', fontSize: '12px' };

// Purchase document type configuration. Purchase Register shows PURCHASE
// documents only (no Purchase Order / GRN — those are non-financial).
const DOC_TYPE_MAP = {
  PI: { label: 'Purchase Invoice', path: '/purchases/purchase-invoice', color: '#800020' },
  NPI: { label: 'Non-Tax Purchase Invoice', path: '/purchases/non-tax-invoice', color: '#800020' },
  IPI: { label: 'Import Purchase Invoice', path: '/purchases/import-invoice', color: '#800020' },
  PR: { label: 'Purchase Return', path: '/purchases/purchase-return', color: '#A0153E' },
  NPR: { label: 'Non-Tax Purchase Return', path: '/purchases/non-tax-purchase-return', color: '#A0153E' },
};

const PURCHASE_TYPE_CODES = ['PI', 'NPI', 'IPI', 'PR', 'NPR'];

// Document-type checkbox options (replaces the old Document Type + Tax dropdowns).
const DOC_TYPE_OPTIONS = [
  { code: 'PI', label: 'Tax Purchase' },
  { code: 'NPI', label: 'Non-Tax Purchase' },
  { code: 'IPI', label: 'Import Purchase' },
  { code: 'PR', label: 'Tax Return' },
  { code: 'NPR', label: 'Non-Tax Return' },
];

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3
];

// "Posted" covers all posted invoices (Posted + Paid + Partial).
const STATUS_OPTIONS = ['All', 'Draft', 'Posted', 'Cancelled'];

const STATUS_COLORS = {
  Draft: { bg: '#FFF3E0', color: '#E65100' },
  Posted: { bg: '#E8F5E9', color: '#2E7D32' },
  Paid: { bg: '#E0F2F1', color: '#00695C' },
  Partial: { bg: '#FFF8E1', color: '#F57F17' },
  Cancelled: { bg: '#FFEBEE', color: '#C62828' },
  Approved: { bg: '#E8EAF6', color: '#283593' },
  Received: { bg: '#E1F5FE', color: '#0277BD' },
  Pending: { bg: '#FFF3E0', color: '#EF6C00' },
  Closed: { bg: '#F3E5F5', color: '#6A1B9A' }
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const PurchaseRegister = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const navigate = useNavigate();
  const location = useLocation();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('purchase-register');

  // Data state
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [vendors, setVendors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [docTypes, setDocTypes] = useState(() => savedMem?.docTypes ?? { PI: true, NPI: true, IPI: true, PR: true, NPR: true });
  const toggleDocType = (code) => setDocTypes(prev => ({ ...prev, [code]: !prev[code] }));
  const [vendorFilter, setVendorFilter] = useState(() => savedMem?.vendorFilter ?? 'All');
  const [jobFilter, setJobFilter] = useState(() => savedMem?.jobFilter ?? 'All');
  const [employeeFilter, setEmployeeFilter] = useState(() => savedMem?.employeeFilter ?? 'All');
  // FLW-05: default to posted documents (Posted / Partial / Paid) — Draft and
  // Cancelled are left out unless picked in the Status filter.
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'Posted');
  const [itemFilter, setItemFilter] = useState(() => savedMem?.itemFilter ?? 'All');
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');
  const [expandedRows, setExpandedRows] = useState(() => savedMem?.expandedRows ?? new Set());
  const toggleRow = (id) => setExpandedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Sort state
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load vendors
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const vendorsRef = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(vendorsRef, orderBy('vendorName', 'asc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // Load jobs
  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const jobRows = await getJobsCached(activeCompany.id);
      setJobs(jobRows.filter(r => r.jobName != null)
        .sort((a, b) => String(a.jobName || '').localeCompare(String(b.jobName || ''))));
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  }, [activeCompany]);

  // Load employees
  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.employeeName != null)
        .sort((a, b) => String(a.employeeName || '').localeCompare(String(b.employeeName || ''))));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  // Load items (for the item filter)
  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const itemRef = collection(db, 'companies', activeCompany.id, 'inventoryItems');
      const snapshot = await getDocs(query(itemRef, orderBy('itemCode')));
      recordReads(activeCompany.id, snapshot.size);
      setItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));
    } catch (error) {
      console.error('Error loading items:', error);
    }
  }, [activeCompany]);

  // Load transactions (purchase types only)
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return [];
    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const q = query(
        transRef,
        where('financialYearId', '==', selectedFYId)
      );
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const allTrans = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      // Filter only purchase type codes
      return allTrans.filter(t => PURCHASE_TYPE_CODES.includes(t.tranTypeCode));
    } catch (error) {
      console.error('Error loading transactions:', error);
      return [];
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => {
    loadFinancialYears();
    loadVendors();
    loadJobs();
    loadEmployees();
    loadItems();
  }, [loadFinancialYears, loadVendors, loadJobs, loadEmployees, loadItems]);

  // Handle incoming navigation state for pre-applied filters
  const processedStateRef = useRef(false);
  useEffect(() => {
    if (processedStateRef.current) return;
    const state = location.state;
    if (!state) return;

    if (state.filterVendorId && vendors.length > 0) {
      const vendor = vendors.find(v => v.id === state.filterVendorId);
      if (vendor) {
        processedStateRef.current = true;
        setVendorFilter(state.filterVendorId);
      }
    }
  }, [location.state, vendors]);

  // Generate report
  const handleGenerateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    setHasGenerated(true);
    try {
      const trans = await loadTransactions();
      setTransactions(trans);
      // Open every invoice's line items by default (no manual expanding).
      setExpandedRows(new Set(trans.filter(t => Array.isArray(t.lines) && t.lines.length > 0).map(t => t.id)));
    } catch (error) {
      console.error('Error generating purchase register:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remember this view (filters + loaded rows) once generated, so Back restores
  // it with 0 re-reads. Filters here are display-only over already-loaded data,
  // so we keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasGenerated) return;
    saveMemory({
      transactions, expandedRows, hasGenerated: true,
      selectedFYId, dateFrom, dateTo, docTypes,
      vendorFilter, jobFilter, employeeFilter, statusFilter, itemFilter,
      searchQuery, sortField, sortDirection
    });
  }, [hasGenerated, transactions, expandedRows, selectedFYId, dateFrom, dateTo,
      docTypes, vendorFilter, jobFilter, employeeFilter, statusFilter, itemFilter,
      searchQuery, sortField, sortDirection, saveMemory]);

  // Filtered and sorted data
  const filteredData = useMemo(() => {
    let data = [...transactions];

    // Date range filter
    if (dateFrom) {
      data = data.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      data = data.filter(t => (t.docDate || '') <= dateTo);
    }

    // Document-type checkboxes (Tax / Non-Tax / Import Purchase + Returns)
    data = data.filter(t => docTypes[t.tranTypeCode]);

    // Item filter — keep only invoices that contain the selected item
    if (itemFilter !== 'All') {
      data = data.filter(t => Array.isArray(t.lines) && t.lines.some(l => l.itemId === itemFilter));
    }

    // Vendor filter
    if (vendorFilter !== 'All') {
      data = data.filter(t => t.partyId === vendorFilter);
    }

    // Job filter
    if (jobFilter !== 'All') {
      data = data.filter(t => t.jobId === jobFilter);
    }

    // Employee filter
    if (employeeFilter !== 'All') {
      data = data.filter(t => t.employeeId === employeeFilter);
    }

    // Status filter — Posted covers Posted + Paid + Partial.
    if (statusFilter !== 'All') {
      const wanted = statusFilter === 'Posted' ? ['posted', 'paid', 'partial'] : [statusFilter.toLowerCase()];
      data = data.filter(t => wanted.includes((t.status || '').toLowerCase()));
    }

    // Search filter (doc number or vendor invoice number)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(t =>
        (t.docNumber || '').toLowerCase().includes(q) ||
        (t.vendorInvoiceNo || '').toLowerCase().includes(q) ||
        (t.partyName || '').toLowerCase().includes(q)
      );
    }

    // Sorting
    data.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'docDate':
          valA = a.docDate || '';
          valB = b.docDate || '';
          break;
        case 'docNumber':
          valA = a.docNumber || '';
          valB = b.docNumber || '';
          break;
        case 'vendorInvoiceNo':
          valA = a.vendorInvoiceNo || '';
          valB = b.vendorInvoiceNo || '';
          break;
        case 'tranTypeCode':
          valA = a.tranTypeCode || '';
          valB = b.tranTypeCode || '';
          break;
        case 'partyName':
          valA = (a.partyName || '').toLowerCase();
          valB = (b.partyName || '').toLowerCase();
          break;
        case 'jobName':
          valA = (a.jobName || '').toLowerCase();
          valB = (b.jobName || '').toLowerCase();
          break;
        case 'employeeName':
          valA = (a.employeeName || '').toLowerCase();
          valB = (b.employeeName || '').toLowerCase();
          break;
        case 'totalAmount':
          valA = a.totalAmount || 0;
          valB = b.totalAmount || 0;
          break;
        case 'totalDiscount':
          valA = a.totalDiscount || 0;
          valB = b.totalDiscount || 0;
          break;
        case 'totalGST':
          valA = a.totalGST || 0;
          valB = b.totalGST || 0;
          break;
        case 'totalWHT':
          valA = a.totalWHT || 0;
          valB = b.totalWHT || 0;
          break;
        case 'netAmount':
          valA = a.netAmount || 0;
          valB = b.netAmount || 0;
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        default:
          valA = a.docDate || '';
          valB = b.docDate || '';
      }

      if (typeof valA === 'string') {
        const cmp = valA.localeCompare(valB);
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });

    return data;
  }, [transactions, dateFrom, dateTo, docTypes, vendorFilter, jobFilter, employeeFilter, statusFilter, itemFilter, searchQuery, sortField, sortDirection]);

  // Summary calculations
  const summary = useMemo(() => {
    const totalDocuments = filteredData.length;
    let totalGross = 0;
    let totalNet = 0;
    let totalGST = 0;
    let totalDiscount = 0;
    let totalWHT = 0;

    filteredData.forEach(t => {
      totalGross += (t.totalAmount || 0);
      totalNet += (t.netAmount || 0);
      totalGST += (t.totalGST || 0);
      totalDiscount += (t.totalDiscount || 0);
      totalWHT += (t.totalWHT || 0);
    });

    return { totalDocuments, totalGross, totalNet, totalGST, totalDiscount, totalWHT };
  }, [filteredData]);

  // Monthly trend data for line chart
  const monthlyTrendData = useMemo(() => {
    const monthMap = {};

    filteredData.forEach(t => {
      if (!t.docDate) return;
      const dateStr = t.docDate;
      const year = dateStr.substring(0, 4);
      const month = parseInt(dateStr.substring(5, 7), 10) - 1;
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;

      if (!monthMap[key]) {
        monthMap[key] = { month: `${MONTH_NAMES[month]} ${year}`, count: 0, amount: 0, sortKey: key };
      }
      monthMap[key].count += 1;
      monthMap[key].amount += (t.netAmount || 0);
    });

    return Object.values(monthMap).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [filteredData]);

  // Amount by document type for bar chart
  const docTypeChartData = useMemo(() => {
    const typeMap = {};

    PURCHASE_TYPE_CODES.forEach(code => {
      typeMap[code] = {
        type: DOC_TYPE_MAP[code]?.label || code,
        gross: 0,
        net: 0,
        count: 0
      };
    });

    filteredData.forEach(t => {
      const code = t.tranTypeCode;
      if (typeMap[code]) {
        typeMap[code].gross += (t.totalAmount || 0);
        typeMap[code].net += (t.netAmount || 0);
        typeMap[code].count += 1;
      }
    });

    return Object.values(typeMap).filter(d => d.count > 0);
  }, [filteredData]);

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Navigate to document
  const handleDocClick = (transaction) => {
    const config = DOC_TYPE_MAP[transaction.tranTypeCode];
    if (config) {
      navigate(config.path, { state: { viewTransactionId: transaction.id } });
    }
  };

  // CSV Export
  const handleExport = () => {
    if (filteredData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Purchase Register\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    if (dateFrom) csv += `Date From: ${dateFrom}\n`;
    if (dateTo) csv += `Date To: ${dateTo}\n`;
    csv += `Generated: ${new Date().toISOString().split('T')[0]}\n\n`;
    csv += `#,Date,Doc Number,Vendor Invoice,Type,Vendor,Job,Employee,Gross Amount,Discount,${taxLabel},WHT,Net Amount,Status\n`;

    filteredData.forEach((t, idx) => {
      csv += `${idx + 1},`;
      csv += `${t.docDate || ''},`;
      csv += `"${(t.docNumber || '').replace(/"/g, '""')}",`;
      csv += `"${(t.vendorInvoiceNo || '').replace(/"/g, '""')}",`;
      csv += `${DOC_TYPE_MAP[t.tranTypeCode]?.label || t.tranTypeCode},`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.jobName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.employeeName || '').replace(/"/g, '""')}",`;
      csv += `${(t.totalAmount || 0).toFixed(2)},`;
      csv += `${(t.totalDiscount || 0).toFixed(2)},`;
      csv += `${(t.totalGST || 0).toFixed(2)},`;
      csv += `${(t.totalWHT || 0).toFixed(2)},`;
      csv += `${(t.netAmount || 0).toFixed(2)},`;
      csv += `${t.status || ''}\n`;
      // Line items under each document
      (Array.isArray(t.lines) ? t.lines : []).forEach(l => {
        const itemLabel = ((l.itemCode ? l.itemCode + ' - ' : '') + (l.itemName || l.description || '')).replace(/"/g, '""');
        csv += `,,,  Item,"${itemLabel}","Qty ${Number(l.quantity) || 0} ${l.unitCode || l.unitOfMeasure || ''}",,${Number(l.unitPrice) || 0},${Number(l.discountAmount) || 0},${Number(l.taxAmount) || 0},${Number(l.lineTotal) || 0},\n`;
      });
    });

    csv += `\n,,,,,,,,${summary.totalGross.toFixed(2)},${summary.totalDiscount.toFixed(2)},${summary.totalGST.toFixed(2)},${summary.totalWHT.toFixed(2)},${summary.totalNet.toFixed(2)},\n`;
    csv += `\nTotal Documents:,${summary.totalDocuments}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Purchase_Register_${selectedFY?.yearCode || 'report'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Purchase Register</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:22px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:20px}
        .summary{display:flex;gap:30px;margin-bottom:20px;padding:15px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:10px;color:#666;margin-bottom:4px}
        .summary-value{font-size:14px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
        th.amount{text-align:right}
        td{padding:5px 8px;border-bottom:1px solid #ddd;font-size:11px}
        td.amount{text-align:right;font-family:'Consolas',monospace}
        .total-row{background:#800020;color:white;font-weight:700}
        .total-row td{border-bottom:none;padding:8px}
        .status-badge{padding:2px 8px;border-radius:10px;font-size:10px;font-weight:500}
        @media print{body{margin:15px}table{font-size:10px}}
      </style></head><body>
      <h1>Purchase Register</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        ${dateFrom ? `From: ${dateFrom}` : ''} ${dateTo ? `To: ${dateTo}` : ''} |
        Generated: ${new Date().toISOString().split('T')[0]}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Total Documents</div>
          <div class="summary-value">${summary.totalDocuments}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Gross Amount</div>
          <div class="summary-value">${formatAmount(summary.totalGross)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Net Amount</div>
          <div class="summary-value">${formatAmount(summary.totalNet)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total ${taxLabel}</div>
          <div class="summary-value">${formatAmount(summary.totalGST)}</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>#</th>
          <th>Date</th>
          <th>Doc #</th>
          <th>Vendor Invoice</th>
          <th>Type</th>
          <th>Vendor</th>
          <th>Job</th>
          <th>Employee</th>
          <th class="amount">Gross</th>
          <th class="amount">Discount</th>
          <th class="amount">${taxLabel}</th>
          <th class="amount">WHT</th>
          <th class="amount">Net Amount</th>
          <th>Status</th>
        </tr></thead>
        <tbody>
          ${filteredData.map((t, idx) => {
            const lines = Array.isArray(t.lines) ? t.lines : [];
            const itemRows = lines.length ? `
              <tr><td></td><td colspan="13" style="padding:4px 8px 8px 24px;background:#fafafa">
                <table style="width:100%;border-collapse:collapse">
                  <thead><tr>
                    <th style="text-align:left;font-size:9px;color:#666;padding:2px 6px">Item</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Qty</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Rate</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Disc</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">${taxLabel}</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Amount</th>
                  </tr></thead>
                  <tbody>${lines.map(l => `<tr>
                    <td style="padding:2px 6px;font-size:10px">${(l.itemCode ? l.itemCode + ' - ' : '') + (l.itemName || l.description || '-')}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${Number(l.quantity) || 0} ${l.unitCode || l.unitOfMeasure || ''}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatAmount(l.unitPrice)}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatAmount(l.discountAmount)}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatAmount(l.taxAmount)}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatAmount(l.lineTotal)}</td>
                  </tr>`).join('')}</tbody>
                </table>
              </td></tr>` : '';
            return `<tr>
            <td>${idx + 1}</td>
            <td>${t.docDate ? formatDate(t.docDate, 'dd MMM yyyy') : '-'}</td>
            <td>${t.docNumber || '-'}</td>
            <td>${t.vendorInvoiceNo || '-'}</td>
            <td>${DOC_TYPE_MAP[t.tranTypeCode]?.label || t.tranTypeCode}</td>
            <td>${t.partyName || '-'}</td>
            <td>${t.jobName || '-'}</td>
            <td>${t.employeeName || '-'}</td>
            <td class="amount">${formatAmount(t.totalAmount)}</td>
            <td class="amount">${formatAmount(t.totalDiscount)}</td>
            <td class="amount">${formatAmount(t.totalGST)}</td>
            <td class="amount">${formatAmount(t.totalWHT)}</td>
            <td class="amount">${formatAmount(t.netAmount)}</td>
            <td>${t.status || '-'}</td>
          </tr>${itemRows}`;
          }).join('')}
          <tr class="total-row">
            <td colspan="8" style="text-align:right">Grand Total</td>
            <td class="amount">${formatAmount(summary.totalGross)}</td>
            <td class="amount">${formatAmount(summary.totalDiscount)}</td>
            <td class="amount">${formatAmount(summary.totalGST)}</td>
            <td class="amount">${formatAmount(summary.totalWHT)}</td>
            <td class="amount">${formatAmount(summary.totalNet)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom tooltip for charts
  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipBox>
          <TooltipTitle>{label}</TooltipTitle>
          {payload.map((p, i) => (
            <TooltipRow key={i}>
              <TooltipDot style={{ background: p.color }} />
              <span>{p.name}: {typeof p.value === 'number' && p.name !== 'Documents' ? formatAmount(p.value) : formatNumber(p.value)}</span>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  // Sort indicator
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FiChevronDown size={12} style={{ opacity: 0.3 }} />;
    return sortDirection === 'asc'
      ? <FiChevronUp size={12} style={{ opacity: 1 }} />
      : <FiChevronDown size={12} style={{ opacity: 1 }} />;
  };

  return (
    <PageLayout pageTitle="Purchase Register" breadcrumb="Reports / Purchase Reports / Purchase Register">
      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Date From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <Label>Date To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup style={{ gridColumn: 'span 2' }}>
            <Label>Document Types</Label>
            <DocTypeChecks>
              {DOC_TYPE_OPTIONS.map(o => (
                <DocCheck key={o.code}>
                  <input type="checkbox" checked={!!docTypes[o.code]} onChange={() => toggleDocType(o.code)} />
                  <span>{o.label}</span>
                </DocCheck>
              ))}
            </DocTypeChecks>
          </FilterGroup>
          <FilterGroup>
            <Label>Item</Label>
            <Select value={itemFilter} onChange={e => setItemFilter(e.target.value)}>
              <option value="All">All Items</option>
              {items.map(it => (
                <option key={it.id} value={it.id}>{it.itemCode ? it.itemCode + ' - ' : ''}{it.itemName}</option>
              ))}
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={vendorFilter}
              onChange={setVendorFilter}
              allOption={{ value: 'All', label: 'All Vendors' }}
            />
          </FilterGroup>
          <FilterGroup>
            <Label>Job / Project</Label>
            <Select value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
              <option value="All">All Jobs</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>{j.jobName}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Employee</Label>
            <Select value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}>
              <option value="All">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeName || emp.name}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <SearchWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by doc number, vendor invoice number, or vendor name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchWrap>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={filteredData.length === 0}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint} disabled={filteredData.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
              <FiBarChart2 /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Summary Cards */}
      {hasGenerated && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIconWrap color={colors.primary.main}><FiHash /></SummaryIconWrap>
            <SummaryInfo>
              <SummaryLabel>Total Documents</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalDocuments)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIconWrap color={colors.chart.accent2}><FiShoppingCart /></SummaryIconWrap>
            <SummaryInfo>
              <SummaryLabel>Total Gross Amount</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalGross)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIconWrap color={colors.chart.tertiary}><FiDollarSign /></SummaryIconWrap>
            <SummaryInfo>
              <SummaryLabel>Total Net Amount</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalNet)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIconWrap color={colors.chart.accent3}><FiPercent /></SummaryIconWrap>
            <SummaryInfo>
              <SummaryLabel>Total {taxLabel}</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalGST)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Loading purchase register...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiFileText /></EmptyIcon>
              <EmptyText>Select a financial year and click Generate to view the purchase register</EmptyText>
            </EmptyState>
          ) : filteredData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiShoppingCart /></EmptyIcon>
              <EmptyText>No purchase documents found for the selected criteria</EmptyText>
              <p style={{ fontSize: '14px', color: colors.text.tertiary, marginTop: 8 }}>
                Try adjusting your filters or date range
              </p>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 40 }}>#</Th>
                  <ThSortable onClick={() => handleSort('docDate')}>
                    Date <SortIcon field="docDate" />
                  </ThSortable>
                  <ThSortable onClick={() => handleSort('docNumber')}>
                    Doc # <SortIcon field="docNumber" />
                  </ThSortable>
                  <ThSortable onClick={() => handleSort('vendorInvoiceNo')}>
                    Vendor Invoice <SortIcon field="vendorInvoiceNo" />
                  </ThSortable>
                  <ThSortable onClick={() => handleSort('tranTypeCode')}>
                    Type <SortIcon field="tranTypeCode" />
                  </ThSortable>
                  <ThSortable onClick={() => handleSort('partyName')}>
                    Vendor <SortIcon field="partyName" />
                  </ThSortable>
                  <ThSortable onClick={() => handleSort('jobName')}>
                    Job <SortIcon field="jobName" />
                  </ThSortable>
                  <ThSortable onClick={() => handleSort('employeeName')}>
                    Employee <SortIcon field="employeeName" />
                  </ThSortable>
                  <ThSortable align="right" onClick={() => handleSort('totalAmount')}>
                    Gross Amount <SortIcon field="totalAmount" />
                  </ThSortable>
                  <ThSortable align="right" onClick={() => handleSort('totalDiscount')}>
                    Discount <SortIcon field="totalDiscount" />
                  </ThSortable>
                  <ThSortable align="right" onClick={() => handleSort('totalGST')}>
                    {taxLabel} <SortIcon field="totalGST" />
                  </ThSortable>
                  <ThSortable align="right" onClick={() => handleSort('totalWHT')}>
                    WHT <SortIcon field="totalWHT" />
                  </ThSortable>
                  <ThSortable align="right" onClick={() => handleSort('netAmount')}>
                    Net Amount <SortIcon field="netAmount" />
                  </ThSortable>
                  <ThSortable onClick={() => handleSort('status')}>
                    Status <SortIcon field="status" />
                  </ThSortable>
                </Tr>
              </Thead>
              <Tbody>
                {filteredData.map((t, idx) => {
                  const statusConfig = STATUS_COLORS[t.status] || { bg: colors.neutral[100], color: colors.text.secondary };
                  const lines = Array.isArray(t.lines) ? t.lines : [];
                  const expanded = expandedRows.has(t.id);
                  return (
                    <React.Fragment key={t.id}>
                    <Tr $clickable onClick={() => handleDocClick(t)}>
                      <Td style={{ color: colors.text.tertiary, cursor: lines.length ? 'pointer' : 'default', whiteSpace: 'nowrap' }} onClick={(e) => { e.stopPropagation(); if (lines.length) toggleRow(t.id); }}>
                        {lines.length ? (expanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />) : null} {idx + 1}
                      </Td>
                      <Td>{t.docDate ? formatDate(t.docDate, 'dd MMM yyyy') : '-'}</Td>
                      <Td>
                        <DocNumber>{t.docNumber || '-'}</DocNumber>
                      </Td>
                      <Td style={{ fontSize: 12, color: colors.text.secondary }}>
                        {t.vendorInvoiceNo || '-'}
                      </Td>
                      <Td>
                        <TypeBadge $color={DOC_TYPE_MAP[t.tranTypeCode]?.color || colors.text.secondary}>
                          {DOC_TYPE_MAP[t.tranTypeCode]?.label || t.tranTypeCode}
                        </TypeBadge>
                      </Td>
                      <Td style={{ maxWidth: 160 }}>
                        <EllipsisText>{t.partyName || '-'}</EllipsisText>
                      </Td>
                      <Td style={{ maxWidth: 120, fontSize: 12, color: colors.text.secondary }}>
                        <EllipsisText>{t.jobName || '-'}</EllipsisText>
                      </Td>
                      <Td style={{ maxWidth: 120, fontSize: 12, color: colors.text.secondary }}>
                        <EllipsisText>{t.employeeName || '-'}</EllipsisText>
                      </Td>
                      <AmountCell>{formatAmount(t.totalAmount)}</AmountCell>
                      <AmountCell style={{ color: (t.totalDiscount || 0) > 0 ? colors.chart.accent3 : colors.text.tertiary }}>
                        {formatAmount(t.totalDiscount)}
                      </AmountCell>
                      <AmountCell>{formatAmount(t.totalGST)}</AmountCell>
                      <AmountCell style={{ color: (t.totalWHT || 0) > 0 ? colors.info.main : colors.text.tertiary }}>
                        {formatAmount(t.totalWHT)}
                      </AmountCell>
                      <AmountCell $bold>{formatAmount(t.netAmount)}</AmountCell>
                      <Td>
                        <StatusBadge $bg={statusConfig.bg} $color={statusConfig.color}>
                          {t.status || '-'}
                        </StatusBadge>
                      </Td>
                    </Tr>
                    {expanded && lines.length > 0 && (
                      <tr>
                        <Td colSpan={14} style={{ padding: 0, background: '#fafafa' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                              <th style={{ ...DETAIL_TH, paddingLeft: 32 }}>Item</th>
                              <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Qty</th>
                              <th style={DETAIL_TH}>Unit</th>
                              <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Rate</th>
                              <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Discount</th>
                              <th style={{ ...DETAIL_TH, textAlign: 'right' }}>{taxLabel}</th>
                              <th style={{ ...DETAIL_TH, textAlign: 'right', paddingRight: 16 }}>Amount</th>
                            </tr></thead>
                            <tbody>
                              {lines.map((l, li) => (
                                <tr key={li}>
                                  <td style={{ ...DETAIL_TD, paddingLeft: 32 }}>{(l.itemCode ? l.itemCode + ' - ' : '') + (l.itemName || l.description || '-')}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatNumber(l.quantity || 0)}</td>
                                  <td style={DETAIL_TD}>{l.unitCode || l.unitOfMeasure || '-'}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatAmount(l.unitPrice)}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatAmount(l.discountAmount)}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatAmount(l.taxAmount)}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right', paddingRight: 16 }}>{formatAmount(l.lineTotal)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={8} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}>
                    Grand Total ({filteredData.length} documents)
                  </Td>
                  <AmountCell $isTotal>{formatAmount(summary.totalGross)}</AmountCell>
                  <AmountCell $isTotal>{formatAmount(summary.totalDiscount)}</AmountCell>
                  <AmountCell $isTotal>{formatAmount(summary.totalGST)}</AmountCell>
                  <AmountCell $isTotal>{formatAmount(summary.totalWHT)}</AmountCell>
                  <AmountCell $isTotal>{formatAmount(summary.totalNet)}</AmountCell>
                  <Td></Td>
                </TotalRow>
              </Tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasGenerated && !loading && filteredData.length > 0 && (
        <ChartsRow>
          <ChartCard>
            <ChartTitle>Monthly Purchase Document Trend</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrendData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                <XAxis dataKey="month" fontSize={11} tick={{ fill: colors.text.secondary }} />
                <YAxis
                  yAxisId="left"
                  fontSize={11}
                  tick={{ fill: colors.text.secondary }}
                  tickFormatter={(v) => {
                    if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
                    if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
                    if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                    return v;
                  }}
                />
                <YAxis yAxisId="right" orientation="right" fontSize={11} tick={{ fill: colors.text.secondary }} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="amount" name="Net Amount" stroke={colors.chart.primary} strokeWidth={2} dot={{ r: 4 }} />
                <Line yAxisId="right" type="monotone" dataKey="count" name="Documents" stroke={colors.chart.accent3} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard>
            <ChartTitle>Amount by Document Type</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={docTypeChartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                <XAxis dataKey="type" fontSize={11} tick={{ fill: colors.text.secondary }} />
                <YAxis fontSize={11} tick={{ fill: colors.text.secondary }} tickFormatter={(v) => {
                  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
                  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
                  if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                  return v;
                }} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend />
                <Bar dataKey="gross" name="Gross Amount" fill={colors.chart.primary} radius={[4, 4, 0, 0]} />
                <Bar dataKey="net" name="Net Amount" fill={colors.chart.accent1} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DocTypeChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 9px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  background: white;
`;

const DocCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: ${colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  input { cursor: pointer; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    border-color: ${colors.primary.main};
    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover:not(:disabled) {
      background: ${colors.neutral[100]};
      border-color: ${colors.primary.main};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 12px;
  box-shadow: ${colors.shadows.md};
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-top: 4px;
`;

const TooltipDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 10px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const ThSortable = styled(Th)`
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[100]};
    color: ${colors.primary.main};
  }

  display: table-cell;

  svg {
    vertical-align: middle;
    margin-left: 4px;
  }
`;

const Tbody = styled.tbody``;

const Tfoot = styled.tfoot``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  ${props => props.$clickable && `
    cursor: pointer;
    &:hover {
      background: ${colors.primary.main}06;
    }
  `}
`;

const Td = styled.td`
  padding: 10px 10px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  text-align: right;
  white-space: nowrap;

  ${props => props.$bold && `
    font-weight: 600;
  `}

  ${props => props.$isTotal && `
    font-weight: 700;
    font-size: 13px;
    color: ${colors.text.primary};
  `}
`;

const DocNumber = styled.span`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.primary.main};
  padding: 2px 6px;
  background: ${colors.primary.main}08;
  border-radius: 3px;
  white-space: nowrap;
`;

const TypeBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  background: ${props => props.$color || colors.primary.main}12;
  color: ${props => props.$color || colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 10px;
  white-space: nowrap;
  background: ${props => props.$bg || colors.neutral[100]};
  color: ${props => props.$color || colors.text.secondary};
`;

const EllipsisText = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px 10px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default PurchaseRegister;
